/**
 * Robust clipboard utility that works across all browsers including mobile Safari
 */

interface ClipboardResult {
  success: boolean;
  error?: string;
}

/**
 * Copies text to clipboard with fallback for mobile Safari and older browsers
 * @param text - The text to copy to clipboard
 * @returns Promise<ClipboardResult> - Object with success status and optional error
 */
export const copyToClipboard = async (
  text: string
): Promise<ClipboardResult> => {
  // Check if we're in a secure context and modern clipboard API is available
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } catch (err) {
      // Modern API failed, fall back to legacy method
      console.warn(
        "Modern clipboard API failed, falling back to legacy method:",
        err
      );
    }
  }

  // Fallback method for mobile Safari and older browsers
  try {
    // Create a temporary textarea element
    const textarea = document.createElement("textarea");
    textarea.value = text;

    // Make the textarea invisible but still focusable
    textarea.style.position = "fixed";
    textarea.style.left = "-999999px";
    textarea.style.top = "-999999px";
    textarea.style.opacity = "0";
    textarea.setAttribute("readonly", "");
    textarea.setAttribute("aria-hidden", "true");

    // Add to DOM
    document.body.appendChild(textarea);

    // For mobile Safari, we need to handle the selection differently
    const isMobileSafari =
      /iPhone|iPad|iPod/.test(navigator.userAgent) &&
      /Safari/.test(navigator.userAgent);

    if (isMobileSafari) {
      // Mobile Safari requires a specific approach
      const range = document.createRange();
      range.selectNodeContents(textarea);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      textarea.setSelectionRange(0, 999999); // For mobile devices
    } else {
      // Standard approach for other browsers
      textarea.focus();
      textarea.select();
    }

    // Execute the copy command
    const successful = document.execCommand("copy");

    // Clean up
    document.body.removeChild(textarea);

    if (successful) {
      return { success: true };
    } else {
      return {
        success: false,
        error: "Copy command failed",
      };
    }
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error occurred",
    };
  }
};

/**
 * Simple wrapper that returns boolean for backward compatibility
 * @param text - The text to copy to clipboard
 * @returns Promise<boolean> - True if successful, false otherwise
 */
export const copyToClipboardSimple = async (text: string): Promise<boolean> => {
  const result = await copyToClipboard(text);
  return result.success;
};
